import React, { forwardRef, useState, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";

import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TablePagination from "@mui/material/TablePagination";
import LinearProgress from "@mui/material/LinearProgress";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";

import EnhancedTableToolbar from "../EnhancedTableToolbar";
import EnhancedTableHead from "../EnhancedTableHead";

import { stableSort, getSorting } from "../../../utils/table";

const Accordion = styled(MuiAccordion)({
  boxShadow: "none",
});

const AccordionSummary = styled(MuiAccordionSummary)({
  marginBottom: -1,
  maxHeight: 0,
  minHeight: 0,
  "&.Mui-expanded": {
    maxHeight: 0,
    minHeight: 0,
  },
  "& .MuiAccordionSummary-content": {
    "&.Mui-expanded": {
      margin: "12px 0",
    },
  },
});

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  paddingBottom: theme.spacing(0),
}));

export function handleSelectAllClick(event, rows, setSelected) {
  if (event.target.checked) {
    const newSelecteds = rows.map(row => row.selectIndex);
    setSelected(newSelecteds);
    return;
  }
  setSelected([]);
}

export function handleRequestSort(event, property, orderBy, order, setOrder, setOrderBy, setReturnOrderBy) {
  const isDesc = orderBy === property && order === "desc";
  setOrder(isDesc ? "asc" : "desc");
  setOrderBy(property);
  setReturnOrderBy(property, isDesc ? "asc" : "desc");
}

export function handleSelected(event, selectIndex, selected, setSelected) {
  const selectedIndex = selected.indexOf(selectIndex);
  let newSelected = [];

  if (selectedIndex === -1) {
    newSelected = newSelected.concat(selected, selectIndex);
  } else if (selectedIndex === 0) {
    newSelected = newSelected.concat(selected.slice(1));
  } else if (selectedIndex === selected.length - 1) {
    newSelected = newSelected.concat(selected.slice(0, -1));
  } else if (selectedIndex > 0) {
    newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
  }
  setSelected(newSelected);
}

export function handleChangePage(event, newPage, setPage, setReturnPage) {
  setPage(newPage);
  setReturnPage(newPage);
}

export function handleChangeRowsPerPage(event, setRowsPerPage, setReturnRowsPerPage) {
  setRowsPerPage(+event.target.value);
  setReturnRowsPerPage(+event.target.value);
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function normalizeRowsPerPage(n) {
  const x = Number(n);
  return ROWS_PER_PAGE_OPTIONS.includes(x) ? x : 10;
}

function getViewportHeight() {
  if (typeof document === "undefined") {
    return 800;
  }
  return document.documentElement.clientHeight;
}

const EnhancedTable = forwardRef((props, ref) => {
  const {
    title,
    rows,
    headRows,
    tableRowComponent: TableRowComponent,
    inProgress,
    deleteHandler,
    filterChildren,
    setReturnOrderBy,
    setReturnPage,
    setReturnRowsPerPage,
    sizeOfRows,
    resetPage,
    pageState,
    rowsPerPageState,
    deleteButton,
    selectAll,
    openFilter,
    callBackExpand,
    stateSortBy,
    allowOrder,
    oderState,
    rowProps,
    settingsCallback,
  } = props;
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState(oderState);
  const [orderBy, setOrderBy] = useState(stateSortBy);
  const [page, setPage] = useState(pageState || 0);

  useEffect(() => {
    if (pageState !== page && pageState !== undefined) setPage(pageState);
  }, [pageState, page, setPage]);
  const [rowsPerPage, setRowsPerPage] = useState(() => normalizeRowsPerPage(rowsPerPageState));
  useEffect(() => {
    const next = normalizeRowsPerPage(rowsPerPageState);
    setRowsPerPage(prev => (prev === next ? prev : next));
  }, [rowsPerPageState]);
  const [expanded, setExpanded] = useState(openFilter || false);
  const dense = false;

  const isSelected = selectIndex => selected.indexOf(selectIndex) !== -1;
  const viewportHeight = getViewportHeight();
  const h = viewportHeight - 64 - 48 - 8;
  const height = viewportHeight - 64 - 48 - 64 - 63 - 1 - (inProgress ? 4 : 0);
  const modRows = rows.map((row, idx) => ({
    ...row,
    selectIndex: idx,
  }));

  const handleChange = expand => {
    callBackExpand(expand);
    setExpanded(expand);
  };

  useImperativeHandle(ref, () => ({
    resetPage() {
      setPage(0);
    },
  }));

  if (resetPage && page !== 0) {
    setPage(0);
  }
  return (
    <Paper
      sx={theme => ({
        display: "flex",
        flexDirection: "column",
        width: "100%",
        mb: 1,
      })}
      style={{ height: `${h}px` }}>
      <EnhancedTableToolbar
        filterList={filterChildren !== undefined}
        title={title}
        selected={selected}
        rows={modRows}
        deleteHandler={rowsToDelete => {
          deleteHandler(rowsToDelete);
          setSelected([]);
        }}
        expandHandler={() => handleChange(openFilter !== undefined ? !openFilter : !expanded)}
        deleteButton={deleteButton}
        settingsCallback={settingsCallback}
      />
      <Accordion square expanded={openFilter !== undefined ? openFilter : expanded && !inProgress}>
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header" />
        {filterChildren && <AccordionDetails>{filterChildren}</AccordionDetails>}
      </Accordion>
      <Box sx={{ overflowY: "auto" }} style={{ height: `${height}px` }}>
        <Table aria-labelledby="tableTitle" size={dense ? "medium" : "small"}>
          <EnhancedTableHead
            selectAll={selectAll}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={event => handleSelectAllClick(event, modRows, setSelected)}
            onRequestSort={(event, property) =>
              handleRequestSort(event, property, orderBy, order, setOrder, setOrderBy, setReturnOrderBy)
            }
            rowCount={modRows.length}
            headRows={headRows}
            allowOrder={allowOrder}
          />

          {!inProgress && (
            <TableBody>
              {stableSort(modRows, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.selectIndex);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRowComponent
                      {...rowProps}
                      key={row.name + labelId}
                      row={row}
                      isItemSelected={isItemSelected}
                      labelId={labelId}
                      handleClick={(event, selectIndex) => handleSelected(event, selectIndex, selected, setSelected)}
                    />
                  );
                })}
            </TableBody>
          )}
        </Table>
      </Box>
      {inProgress && <LinearProgress />}
      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={sizeOfRows || modRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        slotProps={{
          actions: {
            previousButton: {
              "aria-label": "Previous Page",
            },
            nextButton: {
              "aria-label": "Next Page",
            },
          },
        }}
        onPageChange={(event, newPage) => handleChangePage(event, newPage, setPage, setReturnPage)}
        onRowsPerPageChange={event => handleChangeRowsPerPage(event, setRowsPerPage, setReturnRowsPerPage)}
      />
    </Paper>
  );
});

EnhancedTable.defaultProps = {
  inProgress: false,
  setReturnOrderBy: () => null,
  setReturnRowsPerPage: () => null,
  setReturnPage: () => null,
  sizeOfRows: null,
  resetPage: false,
  pageState: undefined,
  rowsPerPageState: 10,
  deleteHandler: () => null,
  deleteButton: true,
  selectAll: true,
  filterChildren: undefined,
  openFilter: undefined,
  callBackExpand: () => null,
  stateSortBy: "userName", // old config passcode
  allowOrder: undefined,
  oderState: "asc",
  rowProps: {},
  settingsCallback: undefined,
};

EnhancedTable.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  headRows: PropTypes.array.isRequired,
  tableRowComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  deleteHandler: PropTypes.func,
  inProgress: PropTypes.bool,
  filterChildren: PropTypes.node,
  setReturnOrderBy: PropTypes.func,
  sizeOfRows: PropTypes.number,
  setReturnPage: PropTypes.func,
  setReturnRowsPerPage: PropTypes.func,
  resetPage: PropTypes.bool,
  pageState: PropTypes.number,
  rowsPerPageState: PropTypes.number,
  deleteButton: PropTypes.bool,
  selectAll: PropTypes.bool,
  openFilter: PropTypes.bool,
  callBackExpand: PropTypes.func,
  stateSortBy: PropTypes.string,
  allowOrder: PropTypes.bool,
  oderState: PropTypes.string,
  rowProps: PropTypes.object,
  settingsCallback: PropTypes.func,
};

export default EnhancedTable;
